"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@ui/lib/utils";

const GAP_PX = 1;
const MIN_BAR_WIDTH_PX = 1;
const FALLBACK_MAX_BARS = 96;

function hashSeed(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0 || 1;
}

export function synthesizeWaveform(seedInput: string, length = 96): number[] {
  const initialSeed = hashSeed(seedInput);
  let seed = initialSeed;
  const phase = (initialSeed % 100) / 30;
  const out: number[] = [];
  for (let i = 0; i < length; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const random = (seed & 0xffff) / 0x10000;
    const envelope = 0.55 + 0.45 * Math.sin((i / length) * Math.PI * 2 + phase);
    out.push(Math.max(0.08, Math.min(1, random * envelope)));
  }
  return out;
}

function clampProgress(value: number) {
  return Math.max(0, Math.min(1, value));
}

/** XI explore payloads can include 300–500+ samples; cap bars to what fits the column. */
export function downsampleWaveform(data: number[], maxBars: number) {
  if (data.length <= maxBars) return data;

  const out: number[] = [];
  for (let i = 0; i < maxBars; i++) {
    const start = Math.floor((i * data.length) / maxBars);
    const end = Math.floor(((i + 1) * data.length) / maxBars);
    let peak = data[start];
    for (let j = start + 1; j < end; j++) {
      if (Math.abs(data[j]) > Math.abs(peak)) peak = data[j];
    }
    out.push(peak);
  }
  return out;
}

function maxBarsForWidth(widthPx: number) {
  if (widthPx <= 0) return FALLBACK_MAX_BARS;
  return Math.max(8, Math.floor((widthPx + GAP_PX) / (MIN_BAR_WIDTH_PX + GAP_PX)));
}

type WaveformInteractionMethod = "keyboard" | "pointer";

export function WaveformBars({
  data,
  className,
  progress = 0,
  onSeekPercent,
  onInteractionStart,
  ariaLabel,
  disabled = false,
}: {
  data: number[];
  className?: string;
  /** Playback progress, 0–1. Bars below this fraction are rendered in the "played" color. */
  progress?: number;
  /** Called with a 0–1 progress value when the interactive waveform is clicked, dragged, or keyboard-seeked. */
  onSeekPercent?: (progress: number) => void;
  onInteractionStart?: (args: { inputMethod: WaveformInteractionMethod; progress: number }) => void;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hoverProgress, setHoverProgress] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = () => setContainerWidth(element.offsetWidth);
    updateWidth();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const displayData = useMemo(() => {
    const maxBars = maxBarsForWidth(containerWidth);
    return downsampleWaveform(data, maxBars);
  }, [containerWidth, data]);

  if (data.length === 0) return null;
  const isInteractive = Boolean(onSeekPercent) && !disabled;
  const max = Math.max(...displayData.map(v => Math.abs(v)), 1);
  const clampedProgress = clampProgress(progress);
  const clampedHoverProgress = hoverProgress == null ? null : clampProgress(hoverProgress);
  const playedCount = Math.min(displayData.length, Math.max(0, Math.floor(clampedProgress * displayData.length)));
  const previewCount =
    clampedHoverProgress == null
      ? playedCount
      : Math.min(displayData.length, Math.max(0, Math.floor(clampedHoverProgress * displayData.length)));

  const progressFromPointer = (clientX: number, currentTarget: HTMLDivElement) => {
    const rect = currentTarget.getBoundingClientRect();
    if (rect.width === 0) return 0;
    return clampProgress((clientX - rect.left) / rect.width);
  };

  const seekFromPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    const nextProgress = progressFromPointer(event.clientX, event.currentTarget);
    setHoverProgress(nextProgress);
    onSeekPercent?.(nextProgress);
  };

  return (
    <div
      ref={containerRef}
      aria-hidden={isInteractive ? undefined : true}
      aria-label={isInteractive ? ariaLabel : undefined}
      aria-valuemax={isInteractive ? 100 : undefined}
      aria-valuemin={isInteractive ? 0 : undefined}
      aria-valuenow={isInteractive ? Math.round(clampedProgress * 100) : undefined}
      className={cn(
        "tw-flex tw-h-6 tw-w-full tw-items-center tw-gap-px",
        isInteractive
          ? "tw-touch-none tw-select-none tw-cursor-pointer tw-outline-none focus-visible:tw-outline-focus"
          : "tw-pointer-events-none",
        className,
      )}
      onKeyDown={
        isInteractive
          ? event => {
              if (event.key === "ArrowRight") {
                event.preventDefault();
                const nextProgress = clampProgress(clampedProgress + 0.05);
                onInteractionStart?.({ inputMethod: "keyboard", progress: nextProgress });
                onSeekPercent?.(nextProgress);
              }
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                const nextProgress = clampProgress(clampedProgress - 0.05);
                onInteractionStart?.({ inputMethod: "keyboard", progress: nextProgress });
                onSeekPercent?.(nextProgress);
              }
              if (event.key === "Home") {
                event.preventDefault();
                onInteractionStart?.({ inputMethod: "keyboard", progress: 0 });
                onSeekPercent?.(0);
              }
              if (event.key === "End") {
                event.preventDefault();
                onInteractionStart?.({ inputMethod: "keyboard", progress: 1 });
                onSeekPercent?.(1);
              }
            }
          : undefined
      }
      onPointerDown={
        isInteractive
          ? event => {
              event.currentTarget.setPointerCapture(event.pointerId);
              setIsDragging(true);
              onInteractionStart?.({
                inputMethod: "pointer",
                progress: progressFromPointer(event.clientX, event.currentTarget),
              });
              seekFromPointer(event);
            }
          : undefined
      }
      onPointerEnter={
        isInteractive ? event => setHoverProgress(progressFromPointer(event.clientX, event.currentTarget)) : undefined
      }
      onPointerCancel={
        isInteractive
          ? () => {
              setIsDragging(false);
              setHoverProgress(null);
            }
          : undefined
      }
      onPointerLeave={isInteractive ? () => setHoverProgress(null) : undefined}
      onPointerMove={
        isInteractive
          ? event => {
              const nextProgress = progressFromPointer(event.clientX, event.currentTarget);
              setHoverProgress(nextProgress);
              if (isDragging) onSeekPercent?.(nextProgress);
            }
          : undefined
      }
      onPointerUp={
        isInteractive
          ? event => {
              setIsDragging(false);
              seekFromPointer(event);
              setHoverProgress(null);
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
              }
            }
          : undefined
      }
      role={isInteractive ? "slider" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
    >
      {displayData.map((value, i) => {
        const normalized = Math.max(0.06, Math.abs(value) / max);
        const isPlayed = i < playedCount;
        const isPreviewed = i < previewCount;
        return (
          <div
            key={i}
            className={cn(
              "tw-flex-1 tw-shrink-0 tw-rounded-full tw-transition-colors",
              isPlayed ? "tw-bg-black" : isPreviewed ? "tw-bg-black/60" : "tw-bg-black/40",
            )}
            style={{ height: `${Math.round(normalized * 100)}%` }}
          />
        );
      })}
    </div>
  );
}
